
import { generateChatCompletion, Tool, ChatMessage, executeCapability } from './openaiService';
import { generateAnthropicCompletion, AnthropicTool, AnthropicMessage, executeCapabilityWithAnthropic } from './anthropicService';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { AgentCapability } from '@/types/agentic';
import { v4 as uuidv4 } from 'uuid';

// Define model provider types
export type ModelProvider = 'openai' | 'anthropic' | 'openrouter';

// Define capability execution options
export interface CapabilityExecutionOptions {
  provider?: ModelProvider;
  model?: string;
  temperature?: number;
  max_tokens?: number;
  stream?: boolean;
}

/**
 * Convert AgentCapability to OpenAI Tool format
 */
export const convertCapabilityToOpenAITool = (capability: AgentCapability): Tool => {
  return {
    type: 'function',
    function: {
      name: capability.name.toLowerCase().replace(/\s+/g, '_'),
      description: capability.description || `Execute the ${capability.name} capability`,
      parameters: capability.config.parameters || {}
    }
  };
};

/**
 * Convert AgentCapability to Anthropic Tool format
 */
export const convertCapabilityToAnthropicTool = (capability: AgentCapability): AnthropicTool => {
  return {
    name: capability.name.toLowerCase().replace(/\s+/g, '_'),
    description: capability.description || `Execute the ${capability.name} capability`,
    input_schema: capability.config.parameters || {}
  };
};

/**
 * Execute a capability using the appropriate model provider
 */
export const executeAgentCapability = async (
  capability: AgentCapability,
  userQuery: string,
  options: CapabilityExecutionOptions = {}
) => {
  const provider = options.provider || 'openai';
  
  try {
    // System prompt based on capability type
    const systemPrompt = getSystemPromptForCapability(capability);
    
    switch (provider) {
      case 'openai':
        // Convert capability to OpenAI tool format
        const openAITool = convertCapabilityToOpenAITool(capability);
        
        // Execute with OpenAI
        return await executeCapability(
          systemPrompt,
          userQuery,
          openAITool,
          {
            model: options.model || 'gpt-4o-mini',
            temperature: options.temperature,
            max_tokens: options.max_tokens,
            stream: options.stream
          }
        );
      
      case 'anthropic':
        // Convert capability to Anthropic tool format
        const anthropicTool = convertCapabilityToAnthropicTool(capability);
        
        // Execute with Anthropic
        return await executeCapabilityWithAnthropic(
          systemPrompt,
          userQuery,
          anthropicTool,
          {
            model: options.model || 'claude-3-5-sonnet-latest',
            temperature: options.temperature,
            max_tokens: options.max_tokens,
            stream: options.stream
          }
        );
      
      case 'openrouter':
        // This would route to the best model through OpenRouter
        // For now, fall back to OpenAI
        toast.info('OpenRouter support coming soon, using OpenAI instead');
        return executeAgentCapability(capability, userQuery, { ...options, provider: 'openai' });
      
      default:
        throw new Error(`Unsupported model provider: ${provider}`);
    }
  } catch (error) {
    console.error('Error executing agent capability:', error);
    toast.error('Failed to execute capability');
    throw error;
  }
};

/**
 * Get appropriate system prompt based on capability type
 */
const getSystemPromptForCapability = (capability: AgentCapability): string => {
  const basePrompt = 'You are an AI assistant that helps with executing capabilities.';
  
  switch (capability.capability_type) {
    case 'course_management':
      return `${basePrompt} You specialize in managing educational courses, understanding module structures, lesson progressions, and educational taxonomies.`;
    
    case 'user_management':
      return `${basePrompt} You specialize in understanding user profiles, learning styles, and personalized educational needs.`;
    
    case 'content_generation':
      return `${basePrompt} You specialize in creating educational content tailored to different learning styles and knowledge levels.`;
    
    case 'data_analysis':
      return `${basePrompt} You specialize in analyzing educational data, identifying patterns, and providing insights.`;
    
    case 'assessment':
      return `${basePrompt} You specialize in creating and evaluating assessments, understanding learning objectives, and measuring progress.`;
    
    case 'notification':
      return `${basePrompt} You specialize in crafting appropriate notifications and reminders for educational contexts.`;
    
    case 'scheduling':
      return `${basePrompt} You specialize in educational scheduling, understanding time constraints, priorities, and optimal learning patterns.`;
    
    default:
      return basePrompt;
  }
};

/**
 * Create a realtime channel for capability execution streaming
 */
export const createCapabilityExecutionChannel = (executionId: string) => {
  return supabase
    .channel(`capability-execution-${executionId}`)
    .subscribe((status) => {
      console.log(`Capability execution channel status: ${status}`);
    });
};

/**
 * Stream capability execution results through a realtime channel
 */
export const streamCapabilityExecution = async (
  executionId: string,
  capability: AgentCapability,
  userQuery: string,
  options: CapabilityExecutionOptions = {}
) => {
  // Create a channel for this execution
  const channel = createCapabilityExecutionChannel(executionId);
  
  try {
    // Start execution
    channel.send({
      type: 'broadcast',
      event: 'execution_status',
      payload: { status: 'started', executionId }
    });
    
    // Execute capability
    const result = await executeAgentCapability(capability, userQuery, {
      ...options,
      stream: false // No streaming for now - would need additional infrastructure
    });
    
    // Send execution result
    channel.send({
      type: 'broadcast',
      event: 'execution_result',
      payload: { result, executionId }
    });
    
    // Complete execution
    channel.send({
      type: 'broadcast',
      event: 'execution_status',
      payload: { status: 'completed', executionId }
    });
    
    return result;
  } catch (error) {
    // Report execution error
    channel.send({
      type: 'broadcast',
      event: 'execution_status',
      payload: { 
        status: 'failed', 
        executionId,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    });
    
    throw error;
  } finally {
    // Remove the channel
    supabase.removeChannel(channel);
  }
};
